import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { CheckboxTile } from './_PdiHelpers';

const GROUPS = [
    {
        key: 'identity',
        i18nKey: 'cfg_pdi_details_group_identity',
        fallback: 'Identity',
        fields: [
            { key: 'idNumber',  i18nKey: 'cfg_pdi_details_id_number',     fallback: 'ID (number)' },
            { key: 'supplier',  i18nKey: 'cfg_pdi_details_id_supplier',   fallback: 'Supplier'    },
            { key: 'name',      i18nKey: 'cfg_pdi_details_id_name',       fallback: 'Name'        },
            { key: 'reference', i18nKey: 'cfg_pdi_details_id_reference',  fallback: 'Reference'   },
            { key: 'variant',   i18nKey: 'cfg_pdi_details_id_variant',    fallback: 'Variant'     },
            { key: 'color',     i18nKey: 'cfg_pdi_details_id_color',      fallback: 'Color'       },
            { key: 'season',    i18nKey: 'cfg_pdi_details_id_season',     fallback: 'Season'      },
        ],
    },
    {
        key: 'categories',
        i18nKey: 'cfg_pdi_details_group_categories',
        fallback: 'Categories',
        fields: [
            { key: 'gender',     i18nKey: 'cfg_pdi_details_cat_gender',     fallback: 'Gender'     },
            { key: 'type',       i18nKey: 'cfg_pdi_details_cat_type',       fallback: 'Type'       },
            { key: 'subtype',    i18nKey: 'cfg_pdi_details_cat_subtype',    fallback: 'Subtype'    },
            { key: 'brand',      i18nKey: 'cfg_pdi_details_cat_brand',      fallback: 'Brand'      },
            { key: 'collection', i18nKey: 'cfg_pdi_details_cat_collection', fallback: 'Collection' },
            { key: 'material',   i18nKey: 'cfg_pdi_details_cat_material',   fallback: 'Material'   },
            { key: 'style',      i18nKey: 'cfg_pdi_details_cat_style',      fallback: 'Style'      },
        ],
    },
    {
        key: 'descriptions',
        i18nKey: 'cfg_pdi_details_group_descriptions',
        fallback: 'Descriptions & Details',
        fields: [
            { key: 'shortDescription', i18nKey: 'cfg_pdi_details_desc_short',       fallback: 'Short description'   },
            { key: 'longDescription',  i18nKey: 'cfg_pdi_details_desc_long',        fallback: 'Long description'    },
            { key: 'marketingCopy',    i18nKey: 'cfg_pdi_details_desc_marketing',   fallback: 'Marketing copy'      },
            { key: 'washInstructions', i18nKey: 'cfg_pdi_details_desc_wash',        fallback: 'Wash instructions'   },
            { key: 'seoDescription',   i18nKey: 'cfg_pdi_details_desc_seo',         fallback: 'SEO description'     },
            { key: 'materialComposition', i18nKey: 'cfg_pdi_details_desc_material_comp', fallback: 'Material composition' },
            { key: 'careLabel',        i18nKey: 'cfg_pdi_details_desc_care_label',  fallback: 'Care label'          },
            { key: 'origin',           i18nKey: 'cfg_pdi_details_desc_origin',      fallback: 'Origin'              },
            { key: 'fitNotes',         i18nKey: 'cfg_pdi_details_desc_fit_notes',   fallback: 'Fit notes'           },
        ],
    },
];

const ProductDetailsListCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const setField = (groupKey, fieldKey, checked) => {
        if (!onChange) return;
        onChange(groupKey, { ...(value[groupKey] || {}), [fieldKey]: checked });
    };

    return (
        <ConfigCard
            title={tr('cfg_pdi_details_title', 'Product details list')}
            description={tr('cfg_pdi_details_desc', 'Pick which product attributes are visible to shoppers in the details list on the product page.')}
            theme={theme}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {GROUPS.map((group) => (
                    <Box key={group.key}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 1 }}>
                            {tr(group.i18nKey, group.fallback)}
                        </Typography>
                        <Stack spacing={0.75}>
                            {group.fields.map((field) => {
                                const groupVal = value[group.key] || {};
                                return (
                                    <CheckboxTile
                                        key={field.key}
                                        label={tr(field.i18nKey, field.fallback)}
                                        checked={!!groupVal[field.key]}
                                        onChange={(v) => setField(group.key, field.key, v)}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                ))}
            </Box>
        </ConfigCard>
    );
};

ProductDetailsListCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ProductDetailsListCard;
